import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";

interface UserTagEntry {
  isLoading: boolean;
  isLoaded: boolean;
  data: UserTagProfileData | null;
}

export interface UserTagState {
  [userId: string]: UserTagEntry;
}

interface SetUserTagLoadingPayload {
  id: string;
}

interface SetUserTagDataPayload {
  id: string;
  data: UserTagProfileData;
}

const userTagSlice = createSlice({
  name: "userTagData",
  initialState: {} as UserTagState,
  reducers: {
    setUserTagLoading: (state: UserTagState, action: PayloadAction<SetUserTagLoadingPayload>) => {
      state[action.payload.id] = {
        isLoading: true,
        isLoaded: false,
        data: null,
      };
    },
    setUserTagData: (state: UserTagState, action: PayloadAction<SetUserTagDataPayload>) => {
      state[action.payload.id] = {
        isLoading: false,
        isLoaded: true,
        data: action.payload.data,
      };
    },
    setUpdatedUserProfileDetails: (state: UserTagState, action: PayloadAction<UserTagProfileData>) => {
      const payload = action.payload;
      const id = payload._id ?? payload.id;
      if (!id) return;
      state[id] = {
        isLoaded: true,
        isLoading: false,
        data: { ...state[id]?.data, ...payload },
      };
    },
  },
});

const { setUserTagLoading: _setUserTagLoadingRaw, setUserTagData: _setUserTagDataRaw } = userTagSlice.actions;

export const { setUpdatedUserProfileDetails } = userTagSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setUserTagLoading = <T extends Exact<SetUserTagLoadingPayload, T>>(payload: T) =>
  _setUserTagLoadingRaw(payload);
export const setUserTagData = <T extends Exact<SetUserTagDataPayload, T>>(payload: T) => _setUserTagDataRaw(payload);

export default userTagSlice.reducer;
